/**
 * Self-scoped DTOs (`docs/design/00-contracts.md` §13.3; C-1, C-29, decision 0001).
 *
 * These endpoints do not go through `can()`: they are pre-tenant or self-scoped, and `can()`
 * requires an actor with an organization (C-20). They are authorized by authentication plus the
 * consent gate plus an explicit self-scope check.
 */
import { z } from 'zod';

import { actorRoleSchema, orgRoleSchema, organizationKindSchema } from '../enums.js';
import { localeSchema } from '../locale.js';
import { accountDeletionSchema } from './artist-access.js';
import { frozenOrganizationSchema } from './control-room-organization-admin.js';
import { actionSchema } from '../policy/types.js';
import {
  pendingConsentSchema,
  personSummarySchema,
  sessionMembershipSchema,
  userAccountSchema,
} from './auth.js';

/**
 * EN ORGANISASJON DER DEN INNLOGGEDE ER GJEST (sak 232, PLAN 1.1): en levende deling av typen
 * `external`, uten medlemskap. Står ved siden av `memberships` og aldri i den, så
 * organisasjonsvelgeren (som bare leser `memberships`) aldri viser en gjesteorganisasjon.
 */
export const guestOrganizationSchema = z
  .object({
    organizationId: z.uuid(),
    organizationName: z.string(),
    organizationKind: organizationKindSchema,
    logoUrl: z.string().nullable(),
  })
  .meta({ id: 'GuestOrganization' });

export const meResponseSchema = z
  .object({
    person: personSummarySchema,
    user: userAccountSchema,
    memberships: z.array(sessionMembershipSchema),
    /** Organisasjonene der personen er gjest (sak 232). Velges bare med `X-Tvitvi-Organization`. */
    guestOrganizations: z.array(guestOrganizationSchema),
    activeOrganizationId: z.uuid().nullable(),
    /**
     * The caller's own role in the active organization, or null when there is none. `guest` when the
     * header names an organisation the caller only has shares in (sak 232).
     */
    activeOrgRole: actorRoleSchema.nullable(),
    /**
     * Every action this role is allowed in the active organization, straight from
     * `role_permissions`; empty when there is no active organization. The browser builds its
     * `Actor` from this and calls the same `can()` the API enforces with. Locked actions appear
     * here only for a superadmin, because they are decided in code and not in the matrix.
     */
    permissions: z.array(actionSchema),
    pendingConsents: z.array(pendingConsentSchema),
    /**
     * Plattformadmin (`app.is_platform_admin()`, 0476): viser Control Room nederst i sidemenyen
     * (sak 238). API-et sender den alltid; valgfri i skjemaet bare så eldre testfiksturer uten feltet
     * fortsatt er en `MeResponse` — mangler den, er svaret nei. Rutene sjekker selv (404 for andre).
     */
    isPlatformAdmin: z.boolean().optional(),
    /**
     * Organisasjonene personen er medlem av og som er fryst (0480). De står aldri i `memberships`;
     * skallet viser «Organisasjonen er fryst» når de er alt personen har. Valgfri av samme grunn som
     * `isPlatformAdmin` — API-et sender den alltid.
     */
    frozenOrganizations: z.array(frozenOrganizationSchema).optional(),
    /**
     * Avdelingene aktøren er med i i den aktive organisasjonen — det samme settet `can()` får på
     * serveren (`resolve_actor`, 0485). Uten det svarte nettleseren nei på hver «egen avdeling»-celle
     * mens API-et sa ja (QA6 K2 #2). Valgfri av samme grunn som `isPlatformAdmin`.
     */
    departmentIds: z.array(z.uuid()).optional(),
    /**
     * 284.5 (1151): personen har bedt om å slette kontoen. Null eller fraværende når kontoen er åpen;
     * ellers er alt annet enn `/v1/me`, innloggingen og «Angre» sperret (`ACCOUNT_CLOSING`).
     * Valgfri av samme grunn som `isPlatformAdmin` — API-et sender den alltid.
     */
    accountDeletion: accountDeletionSchema.nullable().optional(),
    /**
     * B3: kallet kom med et token utenfra (AI eller integrasjon). Tilkoblingen, klienten og
     * organisasjonene den gjelder, med bryterne slik de står nå. Fraværende eller null for nettsiden
     * og appen. MCP-serverens `whoami` leser den.
     */
    external: z
      .object({
        connectionId: z.uuid(),
        clientType: z.enum(['ai', 'integration']),
        clientName: z.string(),
        organizations: z.array(
          z.object({ id: z.uuid(), aiAccess: z.boolean(), aiWrite: z.boolean() }),
        ),
      })
      .nullable()
      .optional(),
  })
  .meta({ id: 'MeResponse' });

export const userSettingsResponseSchema = z
  .object({
    userId: z.uuid(),
    locale: localeSchema,
    timezone: z.string(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'UserSettingsResponse' });

export const updateUserSettingsRequestSchema = z
  .object({
    /**
     * Phase 0 exposes exactly two settings; Norwegian is the default (principle 8). Notification
     * preferences and everything else are later phases and are not accepted here — an unknown key
     * is stripped by Zod, so a client sending one gets 200 and no change.
     */
    locale: localeSchema.optional(),
    timezone: z.string().min(1).max(64).optional(),
  })
  .refine((v) => v.locale !== undefined || v.timezone !== undefined, {
    message: 'Provide at least one of locale or timezone.',
  })
  .meta({ id: 'UpdateUserSettingsRequest' });

/**
 * `PUT /v1/me/active-organization`. A non-membership is 403 `NOT_A_MEMBER` — the same answer
 * whether the organization does not exist or exists without the caller.
 */
export const setActiveOrganizationRequestSchema = z
  .object({ organizationId: z.uuid() })
  .meta({ id: 'SetActiveOrganizationRequest' });

/**
 * `GET /v1/me/profile` — ett menneskes egen rad, lest av mennesket selv (QA12 #7).
 *
 * =================================================================================================
 * HVORFOR DEN IKKE LIGGER PÅ `/v1/me`.
 *
 * `me()` er synkron og leser bare identiteten som alt står på forespørselen — appskallet kaller
 * den på hver eneste sidelast. Å henge en kontaktspørring på den ville lagt en tabell-lesning på
 * den varmeste ruta i systemet for noe bare onboarding-vinduet og profilsiden trenger.
 *
 * =================================================================================================
 * TRE LAG MED HVER SIN EIER, OG DET ER HELE FORMEN PÅ SVARET.
 *
 * · `phone`, `preferredLocale`, `displayName` — mennesket eier dem og kan alltid endre dem.
 * · `email` — LÅST (#4). Den er innloggingen, og det finnes ingen vei til å bytte den ennå.
 * · `jobTitle`, `responsibility`, `reportsToMembershipId`, `role` — huset eier dem.
 *   `canEditMembership` sier om DENNE brukeren har `member_profile:update`, slik at vinduet kan
 *   vise dem som lesefelt framfor å skjule dem. Atle: «De kan redigeres bare hvis brukeren har
 *   rettighet til det i org-en; ellers vises de som lesefelt.»
 *
 * `needsOnboarding` er serverens svar på om vinduet skal opp, ikke nettleserens gjetning: telefon
 * og språk er påkrevd, og det er den samme regelen som PATCH-en håndhever.
 */
export const myProfileDtoSchema = z
  .object({
    contactId: z.uuid().nullable(),
    personId: z.uuid(),
    /*
     * ÉN NAVNEMODELL (QA15 2b): personen har fornavn og etternavn, og `displayName` er det som
     * settes sammen av dem. Alt annet i produktet leser allerede de to — `personSummarySchema`,
     * kontaktregisteret, invitasjonen — og profilvinduet var det siste stedet som fortsatt
     * behandlet navnet som én streng man kunne dele på et mellomrom.
     */
    displayName: z.string(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    email: z.email().nullable(),
    phone: z.string().nullable(),
    preferredLocale: localeSchema.nullable(),
    membershipId: z.uuid().nullable(),
    role: orgRoleSchema.nullable(),
    jobTitle: z.string().nullable(),
    responsibility: z.string().nullable(),
    reportsToMembershipId: z.uuid().nullable(),
    reportsToName: z.string().nullable(),
    canEditMembership: z.boolean(),
    needsOnboarding: z.boolean(),
    /** QA16 funn 30: «Ikke nå» på velkomsten i dette huset, lagret på medlemskapet. */
    welcomeDismissed: z.boolean().optional(),
  })
  .meta({ id: 'MyProfile' });

/**
 * `PATCH /v1/me/profile`. Telefon og språk er alltid tillatt; de tre husfeltene avvises med 403
 * `member_profile:update` når kalleren ikke har cella. E-post står ikke her i det hele tatt —
 * et felt som ikke finnes i kontrakten kan ingen klient sende ved et uhell.
 */
export const updateMyProfileRequestSchema = z
  .object({
    /*
     * TO FELT, IKKE ETT (QA15 2b). `displayName` er utledet av serveren og kan ikke sendes hit:
     * kunne den det, ville navnet hatt to kilder som til slutt ville stått i strid.
     */
    givenName: z.string().trim().min(1).max(100),
    familyName: z.string().trim().max(100),
    phone: z.string().trim().max(50).nullable(),
    preferredLocale: localeSchema.nullable(),
    jobTitle: z.string().trim().max(200).nullable(),
    responsibility: z.string().trim().max(500).nullable(),
    reportsToMembershipId: z.uuid().nullable(),
    /** QA16 funn 30: «Ikke nå» på velkomsten. Bare sann; den kommer ikke tilbake. */
    welcomeDismissed: z.literal(true),
  })
  .partial()
  .meta({ id: 'UpdateMyProfileRequest' });

/**
 * En kobling sett fra den som blir spurt (QA12 #8): hvem som spør, fra hvilket hus — og ingenting
 * om kontaktraden de har ført. Hva huset har skrevet om deg er husets; at de vil koble seg til deg
 * er ditt å svare på.
 */
export const myPersonLinkDtoSchema = z
  .object({
    id: z.uuid(),
    status: z.enum(['pending', 'accepted']),
    organizationName: z.string(),
    requestedByName: z.string(),
    createdAt: z.iso.datetime(),
    respondedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'MyPersonLink' });

export const myPersonLinkListResponseSchema = z
  .object({ items: z.array(myPersonLinkDtoSchema) })
  .meta({ id: 'MyPersonLinkListResponse' });

export type MyPersonLinkDto = z.infer<typeof myPersonLinkDtoSchema>;
export type MyPersonLinkListResponse = z.infer<typeof myPersonLinkListResponseSchema>;
export type MyProfileDto = z.infer<typeof myProfileDtoSchema>;
export type UpdateMyProfileRequest = z.infer<typeof updateMyProfileRequestSchema>;
export type MeResponse = z.infer<typeof meResponseSchema>;
export type GuestOrganization = z.infer<typeof guestOrganizationSchema>;
export type UserSettingsResponse = z.infer<typeof userSettingsResponseSchema>;
export type UpdateUserSettingsRequest = z.infer<typeof updateUserSettingsRequestSchema>;
export type SetActiveOrganizationRequest = z.infer<typeof setActiveOrganizationRequestSchema>;
